// apps/dashboard/src/api/hooks/useSaveBriefAnalysis.ts
import { useAction } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "../../../../../convex/_generated/api";
import { useState, useCallback } from "react";
import type { Id } from "../../../../../convex/_generated/dataModel";
import type { ApiError } from "../types";

interface SaveArgs {
  repositoryId:     Id<"repositories">;
  objective:        string;
  audience:         string;
  constraints?:     string;
  successCriteria:  string;
  preSeedInputs?:   string;
  briefId?:         Id<"briefAnalyses">;
}

export function useSaveBriefAnalysis() {
  // The server function `saveBriefAnalysis` is exported as an action (see
  // convex/sessions/mutations.ts:102), so the client must call it via
  // useAction. Using useMutation here yielded a Convex "Server Error: Trying
  // to execute … as Mutation, but it is defined as Action." that the catch
  // branch surfaced as the misleading "Network error. Please try again."
  // The await/return shape is identical between useMutation and useAction.
  const mutate = useAction(api.sessions.mutations.saveBriefAnalysis);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [lastBriefId, setLastBriefId] = useState<Id<"briefAnalyses"> | null>(null);

  const save = useCallback(async (args: SaveArgs) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await mutate(args);
      setLastBriefId(result.briefId);
    } catch (err: unknown) {
      // Discriminate ConvexError (application error) from ConvexClientError or other
      // platform errors (network failure, WebSocket disconnect). The instanceof check
      // is the runtime-correct discrimination; the prior `as` cast was a compile-time
      // assertion with zero runtime effect (Finding B-12).
      if (err instanceof ConvexError) {
        setError({
          code:         (err.data?.code as string)    ?? "UNKNOWN",
          message:      err.data?.message as string | undefined, // eslint-disable-line @typescript-eslint/no-unnecessary-type-assertion
          retryAfterMs: err.data?.retryAfterMs as number | undefined,
        });
      } else {
        // Non-ConvexError. The backend's E.error() helper (convex/helpers/errors.ts)
        // throws plain Error objects whose `.code`/`.extra` don't survive
        // Convex's wire protocol — only the message string does. The server
        // wraps it in a "[CONVEX M(…)] Server Error\nUncaught Error: …" envelope.
        // Strip the envelope and surface the real message; only fall back to
        // "Network error" if the message is unrecognisable.
        console.error("[useSaveBriefAnalysis] non-ConvexError exception:", err); // eslint-disable-line no-console
        let surfaceMessage = "Network error. Please try again.";
        if (err instanceof Error && err.message) {
          const inner = err.message
            .replace(/^\[CONVEX[^\n]*Server Error[\r\n]+/i, "")
            .replace(/^(?:Uncaught (?:Error|ConvexError):\s*)+/i, "")
            .replace(/\n\s*Called by client[\s\S]*$/m, "")
            .trim();
          if (inner.length > 0 && inner.length < 240) {
            surfaceMessage = inner;
          }
        }
        setError({
          code:    "PLATFORM_ERROR",
          message: surfaceMessage,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [mutate]);

  return { save, isSaving, error, lastBriefId };
}
